import '../../common/bn-property';
import '../../common/qr-scanner';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, SirenActionMixin, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class WorkOrderBinSerial extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.currentSerialNumber = '';
    this.serialNumber = '';
  }

  static get properties() {
    return {
      currentSerialNumber: { type: String, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      .actions {
        margin-top: 1em;
      }
      .actions .button {
        margin: 8px 0;
      }
      .prase-error {
        font-weight: bold;
        color: var(--red-20);
      }
    `];
  }

  render() {
    return html`
      <lit-entity .href="${this.binHref}" @update="${this._onUpdateBin}"></lit-entity>
      <bn-flash></bn-flash>
      ${this.currentSerialNumber ? this._renderCurrentSerial() : this._renderAttachSerial()}
    `;
  }

  _renderCurrentSerial() {
    return html`
      <bn-property label="Serial Number">
        ${this.currentSerialNumber}
      </bn-property>
      <div class="actions">
        <button class="button button--contained" @click="${this._removeSerial}">
          Replace BinSentry QR Code Sticker
        </button>
      </div>
    `;
  }

  _renderAttachSerial() {
    return html`
      <p class="subdued">
        Place a BinSentry QR code sticker on the bin in a location that is clearly visible.
      </p>
      <p class="subdued">
        Use your mobile device's camera to scan the QR code located on the BinSentry sticker.
      </p>
      <qr-scanner
        @data="${this._parseBinQr}"
        @start="${this._clear}"
        @stop="${this._clear}"
        @error="${(e) => this._error(e.detail.error.message)}"
      ></qr-scanner>
      <div class="manual-input">
        <bn-form-field
          type="text"
          name="serialNumber"
          title="Bin Sticker Serial Number"
          .value="${this.serialNumber}"
          @input="${(e) => this.serialNumber = e.target.input.value.toLowerCase()}"
        ></bn-form-field>
        <div class="actions">
          <button class="button button--contained" @click="${this._saveSerial}">Update</button>
        </div>
      </div>
    `;
  }

  _clear() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
  }

  _error(error) {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    flash.critical(error);
  }

  _success(msg) {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    flash.success(msg);
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.binHref = this.getHref('https://api.binsentry.com/rel/bin');
  }

  _onUpdateBin(e) {
    const { entity } = e.detail;
    this.binEntity = entity;
    this.currentSerialNumber = entity?.properties.serialNumber;
    this.serialNumber = this.currentSerialNumber;
  }

  async _parseBinQr(e) {
    const { data } = e.detail;
    if (this.processing || !data) {
      return;
    }
    try {
      this.processing = true;
      const url = new URL(data);
      if (!url.hostname.includes('binsentry.com')) {
        throw new Error('Unsupported hostname.');
      }
      if (!url.pathname.startsWith('/bins')) {
        throw new Error('Incorrect path name.');
      }
      this.serialNumber = url.searchParams.get('serialNumber');
      if (this.serialNumber === this.binEntity?.properties.serialNumber) {
        this._success('This serial number is already linked to this bin.');
        return;
      }
      await this._saveSerial();
      this._success('The BinSentry serial number has been successfully linked to the bin.');
    } catch (ex) {
      this._error(html`
        The QR code scanned does not appear to be for a BinSentry bin serial number.
        <div><code>${ex.message}</code></div>
      `);
    } finally {
      this.processing = undefined;
    }
  }

  async _saveSerial() {
    try {
      const action = this.entity.getActionByName('add-serial-number');
      action.getFieldByName('serialNumber').value = this.serialNumber;
      await this.submitAction(action);
      entityStore.invalidateHref(this.href);
      entityStore.invalidateHref(this.binHref);
      this.currentSerialNumber = this.serialNumber;
    } catch (error) {
      this._error(html`
        There was a problem linking the BinSentry serial number to the bin.
        <div><code>${error.message}</code></div>
      `);
      this.currentSerialNumber = '';
    }
  }

  _removeSerial() {
    this.currentSerialNumber = '';
  }
}

window.customElements.define('work-order-bin-serial', WorkOrderBinSerial);
